<template>
  <div>
    <!-- Newsletter Subscription Section -->
    <NewsletterFooter />

    <!-- Main Footer -->
    <footer class="bg-gray-950" aria-labelledby="footer-heading">
      <h2 id="footer-heading" class="sr-only">Footer</h2>
      <div class="mx-auto w-full max-w-screen-2xl py-16 px-4 sm:px-6 lg:px-8">
        <div class="border-gray-300/10">
          <div class="grid grid-cols-4 gap-8 text-white border-b border-gray-300/10 pb-8">

            <div class="col-span-4 w-full md:col-span-2 lg:col-span-1 flex flex-col gap-7">
              <NuxtLink :to="localePath('/')" class="flex w-auto shrink-0">
                <Logo class="max-w-52 lg:max-w-60 xl:max-w-68" />
              </NuxtLink>
              <p v-if="slogan" class="text-base text-white">
                {{ slogan }}
              </p>
              <div v-if="socials" class="flex justify-start">
                <div class="flex items-center gap-6">
                  <a
                    v-for="(socialItem, i) in socials"
                    :href="socialItem.theurl"
                    :key="`social-${i}`"
                    class="rounded w-9 h-9 flex items-center justify-center hover:bg-gray-300/10"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <NuxtImg
                      :title="socialItem.alias"
                      class="w-9 h-9"
                      :src="getCmsImage(socialItem.icon)"
                      format="webp"
                      :alt="socialItem.alias"
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>
            </div>

            <div v-for="submenu in menu" :key="submenu.title" class="col-span-4 w-full md:col-span-2 lg:col-span-1">
              <h3 class="text-sm font-semibold leading-6 text-gray-100">
                {{ submenu.title }}
              </h3>
              <ul role="list" class="mt-5 space-y-4">
                <template  v-for="item in submenu.children">
                  <li :key="item.id" v-if="item.callToAction && item.slug != 'resi-e-rimborsi'">
                    <NuxtLink
                      v-if="item.callToAction?.element"
                      :to="localePath('/' + item.callToAction.element.slug)"
                      class="text-sm leading-6 text-gray-200 hover:text-gray-300"
                    >
                      {{ item.title }}
                    </NuxtLink>
                    <a
                      class="text-sm leading-6 text-gray-200 hover:text-gray-300"
                      v-else-if="item.callToAction?.url"
                      :class="[isMailTo(item.callToAction.url) ? 'underline decoration-dotted' : '' ]"
                      :href="item.callToAction.url"
                    >
                      {{ item.title }}
                    </a>
                    <span class="text-sm leading-6 text-gray-200 hover:text-gray-300" v-else>
                      {{ item.title }}
                    </span>
                  </li>
                </template>
              </ul>
            </div>

            <div class="col-span-4 w-full md:col-span-2 lg:col-span-1">
              <h3 class="text-sm font-semibold leading-6 text-gray-100 uppercase">
                {{ t('Account') }}
              </h3>
              <ul role="list" class="mt-5 space-y-4">
                <li v-for="item in account" :key="item.id">
                  <NuxtLink :to="item.slug" class="text-sm leading-6 text-gray-200 hover:text-gray-300">{{
                    item.title
                  }}</NuxtLink>
                </li>
              </ul>
            </div>

          </div>

          <div v-if="categories.length" class="text-white pt-8 mb-4">
            <!-- Mobile: Accordion -->
            <Accordion type="multiple" class="lg:hidden">
              <AccordionItem v-for="category in categories" :key="category.slug" :value="category.slug" class="border-b border-gray-300/10">
                <AccordionTrigger class="text-sm font-semibold text-gray-100 hover:no-underline py-3">
                  <NuxtLink v-if="category.callToAction?.url" :to="localePath(toPath(category.callToAction.url))" class="hover:text-gray-300" @click.stop>
                    {{ category.title }}
                  </NuxtLink>
                  <span v-else>{{ category.title }}</span>
                </AccordionTrigger>
                <AccordionContent v-if="category.children?.length">
                  <ul role="list" class="space-y-4 pb-2">
                    <li v-for="child in category.children" :key="child.slug">
                      <NuxtLink
                        v-if="child.callToAction?.url"
                        :to="localePath(toPath(child.callToAction.url))"
                        class="text-sm leading-6 text-gray-200 hover:text-gray-300"
                      >
                        {{ child.title }}
                      </NuxtLink>
                      <span v-else class="text-sm leading-6 text-gray-200">{{ child.title }}</span>
                      <ul v-if="child.children?.length" role="list" class="mt-2 ml-3 space-y-2">
                        <li v-for="grandchild in child.children" :key="grandchild.slug">
                          <NuxtLink
                            v-if="grandchild.callToAction?.url"
                            :to="localePath(toPath(grandchild.callToAction.url))"
                            class="text-xs leading-5 text-gray-300 hover:text-gray-400"
                          >
                            {{ grandchild.title }}
                          </NuxtLink>
                          <span v-else class="text-xs leading-5 text-gray-300">{{ grandchild.title }}</span>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <!-- Desktop: Grid -->
            <div class="hidden lg:grid grid-cols-4 gap-8 mb-4">
              <div v-for="category in categories" :key="category.slug" class="col-span-1">
                <h3 class="text-sm font-semibold leading-6 text-gray-100">
                  <NuxtLink v-if="category.callToAction?.url" :to="localePath(toPath(category.callToAction.url))" class="hover:text-gray-300">
                    {{ category.title }}
                  </NuxtLink>
                  <span v-else>{{ category.title }}</span>
                </h3>
                <ul v-if="category.children?.length" role="list" class="mt-5 space-y-4">
                  <li v-for="child in category.children" :key="child.slug">
                    <NuxtLink
                      v-if="child.callToAction?.url"
                      :to="localePath(toPath(child.callToAction.url))"
                      class="text-sm leading-6 text-gray-200 hover:text-gray-300"
                    >
                      {{ child.title }}
                    </NuxtLink>
                    <span v-else class="text-sm leading-6 text-gray-200">{{ child.title }}</span>
                    <ul v-if="child.children?.length" role="list" class="mt-2 ml-3 space-y-2">
                      <li v-for="grandchild in child.children" :key="grandchild.slug">
                        <NuxtLink
                          v-if="grandchild.callToAction?.url"
                          :to="localePath(toPath(grandchild.callToAction.url))"
                          class="text-xs leading-5 text-gray-300 hover:text-gray-400"
                        >
                          {{ grandchild.title }}
                        </NuxtLink>
                        <span v-else class="text-xs leading-5 text-gray-300">{{ grandchild.title }}</span>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>

        </div>

        <div class="border-t text-xs border-gray-900/10 pt-8 flex flex-col gap-8">
          <div class="flex flex-col xl:flex-row w-full justify-between items-center gap-8">
            <CheckoutPaymentFooterIcons class="xl:justify-start max-w-full" />

            <ShippingMethodIcons class="xl:justify-end max-w-full" />
          </div>
          <div class="text-xs xl:text-sm leading-5 text-gray-400 md:order-1 text-center">
            {{ copyright }}
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script lang="ts" setup>
import { getCmsImage } from '@/composables/useCms';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import CheckoutPaymentFooterIcons from '../CheckoutPayment/CheckoutPaymentFooterIcons.vue';
import ShippingMethodIcons from '../ShippingMethod/ShippingMethodIcons.vue';
import NewsletterFooter from '@/components/NewsletterFooter.vue';

const localePath = useLocalePath();
const { t, locale } = useI18n();
const { socials, menu, categories, copyright, slogan } = useFooter();

const isMailTo = (url: string) => url.toLowerCase().startsWith('mailto:');
const toPath = (url: string) => {
  try { return new URL(url).pathname; } catch { return url; }
};

const account = computed(() => [
  {
    id: 'my-account',
    title: t('My account'),
    slug: localePath('/my-account'),
  },
  {
    id: 'order-history',
    title: t('Order history'),
    slug: localePath('/my-account/orders'),
  },
  {
    id: 'login',
    title: t('Login'),
    slug: localePath('/login'),
  },
  {
    id: 'register',
    title: t('Register'),
    slug: localePath('/register'),
  },
]);
</script>
